import React, { useState, useEffect, useRef } from 'react';
import VIModal from '../../../common/VIModal/VIModal';
import { PostProps } from '../../../../types/postType';
import { UserType } from '../../../../types/userType';
import { fetchShippingInfo, updateShippingInfo } from '../../../../api/profileServices';
import PostReplyInput from './PostReplyInput';
import apiClient from '../../../../api/apiClient';
import { ENDPOINTS } from '../../../../api/endpoints';
import { Country, State, City, ICountry, IState, ICity } from 'country-state-city';
import DropdownIcon from '../../../../assets/icons/icon_dropdown.svg';
import './ApplyCollabsStepsModal.css';
import MediaManager from './MediaManager';
import Camera from '../../../common/Camera/Camera';
import GiphyModal from '../../../common/GiphyModal/GiphyModal';
import VIToast from '../../../common/VIToast/VIToast';
import { processCameraCapture, processMediaFiles } from '../../../../services/mediaServices';
import icon_newpost_images from '../../../../assets/icons/icon_newpost_images.svg';
import icon_newpost_camera from '../../../../assets/icons/icon_newpost_camera.svg';
import icon_newpost_gif from '../../../../assets/icons/icon_newpost_gif.svg';
import { PostMedia } from '../../../../types/postType';
import PostReply from './PostReply';
import { identifyTikTokUser, trackCompleteRegistration } from '../../../../utils/tiktokTracking';
import { createPortal } from 'react-dom';

// Updated interface to match the API response structure
interface ShippingInfo {
  id?: string;
  first_name: string;
  last_name: string;
  email: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  phone_number: string;
}

interface TikTokVerificationData {
  tiktok_username: string;
  follower_count: number;
  verification_image: string;
  is_verified: boolean;
  verified_at: string;
}

interface ApplyCollabsStepsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (text: string, images: File[], gif: string | null) => Promise<void>;
  originalPost: PostProps;
  currentUser: UserType | null;
}

const ApplyCollabsStepsModal: React.FC<ApplyCollabsStepsModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  originalPost,
  currentUser
}) => {
  // TikTok verification states
  const [step, setStep] = useState(1);
  const [tiktokScreenshot, setTiktokScreenshot] = useState<File | null>(null);
  const [tiktokScreenshotUrl, setTiktokScreenshotUrl] = useState<string | null>(null);
  const [tiktokVerified, setTiktokVerified] = useState(false);
  const [tiktokVerifying, setTiktokVerifying] = useState(false);
  const [tiktokError, setTiktokError] = useState<string | null>(null);
  const [tiktokUsername, setTiktokUsername] = useState<string | null>(null);
  const [tiktokFollowers, setTiktokFollowers] = useState<number | null>(null);
  const [tiktokVerificationImage, setTiktokVerificationImage] = useState<string | null>(null);
  const [checkingPreVerification, setCheckingPreVerification] = useState(true);
  const [confirmingIdentity, setConfirmingIdentity] = useState(false);
  const [confirmationSelection, setConfirmationSelection] = useState<'yes' | 'no' | null>(null);
  
  // Shipping info states
  const [shippingInfo, setShippingInfo] = useState<ShippingInfo>({
    first_name: '',
    last_name: '',
    email: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    phone_number: ''
  });
  const [loadingShipping, setLoadingShipping] = useState(false);
  const [shippingError, setShippingError] = useState<string | null>(null);
  const [shippingComplete, setShippingComplete] = useState(false);
  const [shippingFormTouched, setShippingFormTouched] = useState(false);
  const [shippingInfoValid, setShippingInfoValid] = useState(false);
  const [updatingShipping, setUpdatingShipping] = useState(false);
  
  // Reply states
  const [replyText, setReplyText] = useState('');
  const [replyMedia, setReplyMedia] = useState<File[]>([]);
  const [replyGif, setReplyGif] = useState<string | null>(null);
  const [replyError, setReplyError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Additional shipping form state for dropdowns
  const [countries, setCountries] = useState<ICountry[]>([]);
  const [states, setStates] = useState<IState[]>([]);
  const [cities, setCities] = useState<ICity[]>([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [customCountry, setCustomCountry] = useState('');
  const [customState, setCustomState] = useState('');
  const [customCity, setCustomCity] = useState('');

  // New states for reply section
  const [replyContent, setReplyContent] = useState('');
  const [mediaList, setMediaList] = useState<PostMedia[]>([]);
  const [selectedGif, setSelectedGif] = useState<string | null>(null);
  const [showCamera, setShowCamera] = useState(false);
  const [showGiphyModal, setShowGiphyModal] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [isPosting, setIsPosting] = useState(false);
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  // Load shipping info when step 2 is reached
  useEffect(() => {
    if (step === 2) {
      loadShippingInfo();
    }
  }, [step]);

  // Check if user is already verified when component mounts
  useEffect(() => {
    checkTikTokVerificationStatus();
  }, []);

  // Validate shipping info whenever it changes
  useEffect(() => {
    const isValid = validateShippingInfo(shippingInfo);
    setShippingInfoValid(isValid);
    
    if (isValid && shippingFormTouched) {
      setShippingComplete(true);
    } else {
      setShippingComplete(false);
    }
  }, [shippingInfo, shippingFormTouched]);

  // Load countries on component mount
  useEffect(() => {
    setCountries(Country.getAllCountries());
  }, []);

  // Load states when country changes
  useEffect(() => {
    if (selectedCountry && selectedCountry !== 'custom') {
      setStates(State.getStatesOfCountry(selectedCountry));
    } else {
      setStates([]);
    }
  }, [selectedCountry]);

  // Load cities when state changes
  useEffect(() => {
    if (selectedCountry && selectedState && selectedCountry !== 'custom' && selectedState !== 'custom') {
      setCities(City.getCitiesOfState(selectedCountry, selectedState));
    } else {
      setCities([]);
    }
  }, [selectedCountry, selectedState]);

  // Add this effect to validate when the submit button should be enabled
  useEffect(() => {
    // Enable submit button if there's content or media
    setIsSubmitEnabled(
      (replyContent.trim().length > 0 && replyContent.trim().length <= 1000) || 
      mediaList.length > 0 || 
      selectedGif !== null
    );
  }, [replyContent, mediaList, selectedGif]);

  const validateShippingInfo = (info: ShippingInfo): boolean => {
    return !!(
      info.first_name && 
      info.last_name && 
      info.email &&
      info.address_line_1 && 
      info.city && 
      info.state && 
      info.postal_code && 
      info.country && 
      info.phone_number
    );
  };

  const checkTikTokVerificationStatus = async () => {
    try {
      setCheckingPreVerification(true);
      const response = await apiClient.get(ENDPOINTS.TIKTOK_VERIFY);
      const { data } = response.data;
      
      if (data.is_verified) {
        // User is already verified
        setTiktokVerified(true);
        setTiktokUsername(data.tiktok_username);
        setTiktokFollowers(data.follower_count);
        setTiktokVerificationImage(data.verification_image);
        setConfirmingIdentity(true);
      }
    } catch (error) {
      // User is not verified, just continue with the normal flow
      console.log('User does not have a verified TikTok profile yet');
    } finally {
      setCheckingPreVerification(false);
    }
  };

  const confirmTikTokIdentity = (confirm: boolean) => {
    if (confirm) {
      // User confirmed this is their TikTok profile
      setConfirmationSelection('yes');
      setTimeout(() => {
        setConfirmingIdentity(false);
      }, 300);
    } else {
      // User rejected, reset verification
      setConfirmationSelection('no');
      setTimeout(() => {
        setTiktokVerified(false);
        setTiktokUsername(null);
        setTiktokFollowers(null);
        setTiktokVerificationImage(null);
        setConfirmingIdentity(false);
      }, 300);
    }
  };

  // Handle country selection change
  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedCountry(value);
    setSelectedState('');
    setSelectedCity('');
    
    setShippingFormTouched(true);
    
    // Update shipping info with the new country
    setShippingInfo(prev => ({
      ...prev,
      country: value === 'custom' ? customCountry : getCountryNameFromCode(value)
    }));
  };

  // Get country name from code
  const getCountryNameFromCode = (code: string): string => {
    const country = countries.find(c => c.isoCode === code);
    return country ? country.name : '';
  };

  // Handle state selection change
  const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedState(value);
    setSelectedCity('');
    
    setShippingFormTouched(true);
    
    // Update shipping info with the new state
    setShippingInfo(prev => ({
      ...prev,
      state: value === 'custom' ? customState : getStateNameFromCode(selectedCountry, value)
    }));
  };

  // Get state name from code
  const getStateNameFromCode = (countryCode: string, stateCode: string): string => {
    const state = states.find(s => s.isoCode === stateCode);
    return state ? state.name : '';
  };

  // Handle city selection change
  const handleCityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedCity(value);
    
    setShippingFormTouched(true);
    
    // Update shipping info with the new city
    setShippingInfo(prev => ({
      ...prev,
      city: value === 'custom' ? customCity : value
    }));
  };

  // Handle custom country input
  const handleCustomCountryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomCountry(value);
    setShippingFormTouched(true);
    
    // Update shipping info with the custom country
    setShippingInfo(prev => ({
      ...prev,
      country: value
    }));
  };

  // Handle custom state input
  const handleCustomStateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomState(value);
    setShippingFormTouched(true);
    
    // Update shipping info with the custom state
    setShippingInfo(prev => ({
      ...prev,
      state: value
    }));
  };

  // Handle custom city input
  const handleCustomCityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomCity(value);
    setShippingFormTouched(true);
    
    // Update shipping info with the custom city
    setShippingInfo(prev => ({
      ...prev,
      city: value
    }));
  };

  // Load shipping info and populate dropdown selections
  const loadShippingInfo = async () => {
    try {
      setLoadingShipping(true);
      setShippingError(null);
      
      const response = await apiClient.get(ENDPOINTS.USER_SHIPPING_INFO);
      const shippingData = response.data.data;
      
      if (shippingData) {
        // Split the name into first and last name
        const nameParts = shippingData.name.split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        
        // Set the shipping info state
        setShippingInfo({
          first_name: firstName,
          last_name: lastName,
          email: shippingData.email,
          address_line_1: shippingData.shipping_address_line1,
          address_line_2: shippingData.shipping_address_line2 || '',
          city: shippingData.shipping_city,
          state: shippingData.shipping_state,
          postal_code: shippingData.shipping_zip,
          country: '', // Will be set when we find the country by code
          phone_number: shippingData.phone_number
        });
        
        // Find and set country dropdown
        const country = countries.find(c => c.isoCode === shippingData.country_code);
        if (country) {
          setSelectedCountry(country.isoCode);
          setShippingInfo(prev => ({
            ...prev,
            country: country.name
          }));
          
          // After setting country, load the states for this country
          const countryStates = State.getStatesOfCountry(country.isoCode);
          
          // Try to find state by name in the loaded states
          const matchingState = countryStates.find(
            s => s.name.toLowerCase() === shippingData.shipping_state.toLowerCase()
          );
          
          if (matchingState) {
            // Found state in our dropdown options
            setSelectedState(matchingState.isoCode);
            
            // Now load cities for this state
            const stateCities = City.getCitiesOfState(country.isoCode, matchingState.isoCode);
            
            // Try to find city by name
            const matchingCity = stateCities.find(
              c => c.name.toLowerCase() === shippingData.shipping_city.toLowerCase()
            );
            
            if (matchingCity) {
              // Found city in our dropdown options
              setSelectedCity(matchingCity.name);
            } else {
              // City not found in dropdown, use custom
              setSelectedCity('custom');
              setCustomCity(shippingData.shipping_city);
            }
          } else {
            // State not found in dropdown, use custom
            setSelectedState('custom');
            setCustomState(shippingData.shipping_state);
          }
        } else {
          // Country not found in dropdown, use custom
          setSelectedCountry('custom');
          setCustomCountry(shippingData.country_code);
          setShippingInfo(prev => ({
            ...prev,
            country: shippingData.country_code
          }));
        }
        
        // Set form as initially untouched since we just loaded it
        setShippingFormTouched(false);
      }
    } catch (error) {
      console.error('Error loading shipping info:', error);
      setShippingError('Failed to load your shipping information. Please enter it manually.');
    } finally {
      setLoadingShipping(false);
    }
  };

  const handleShippingInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setShippingInfo(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Mark form as touched when user starts editing
    if (!shippingFormTouched) {
      setShippingFormTouched(true);
    }
  };

  const saveShippingInfo = async (): Promise<boolean> => {
    if (!validateShippingInfo(shippingInfo)) {
      setShippingError('Please fill in all required fields.');
      return false;
    }
    
    try {
      setShippingError(null);
      
      // Format payload for API
      const payload = {
        first_name: shippingInfo.first_name,
        last_name: shippingInfo.last_name,
        email: shippingInfo.email,
        address_line_1: shippingInfo.address_line_1,
        address_line_2: shippingInfo.address_line_2,
        city: shippingInfo.city,
        state: shippingInfo.state,
        postal_code: shippingInfo.postal_code,
        country: shippingInfo.country,
        phone_number: shippingInfo.phone_number
      };
      
      await updateShippingInfo(payload);
      setShippingComplete(true);
      return true;
    } catch (error) {
      console.error('Error saving shipping info:', error);
      setShippingError('Failed to save shipping information. Please try again.');
      return false;
    }
  };

  const handleTiktokScreenshotSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setTiktokScreenshot(file);
      setTiktokScreenshotUrl(URL.createObjectURL(file));
      setTiktokError(null);
    }
  };

  const verifyTiktokScreenshot = async () => {
    if (!tiktokScreenshot) {
      setTiktokError('Please select a screenshot first.');
      return;
    }

    setTiktokVerifying(true);
    setTiktokError(null);

    const formData = new FormData();
    formData.append('profile_image', tiktokScreenshot);

    try {
      const response = await apiClient.post(ENDPOINTS.TIKTOK_VERIFY, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const { data } = response.data;
      setTiktokVerified(true);
      setTiktokUsername(data.tiktok_username);
      setTiktokFollowers(data.follower_count);
      setTiktokVerificationImage(data.verification_image);
    } catch (error) {
      console.error('Error verifying TikTok profile:', error);
      setTiktokError('We couldn\'t verify your TikTok profile. Please ensure your screenshot clearly shows your username and follower count.');
    } finally {
      setTiktokVerifying(false);
    }
  };

  const handleReplyTextChange = (text: string) => {
    setReplyText(text);
    setReplyError(null);
  };

  const handleReplyMediaSelect = (files: File[]) => {
    setReplyMedia(files);
  };

  const handleStepBack = () => {
    if (step === 1) {
      onClose();
    } else {
      setStep(step - 1);
    }
  };

  const handleStepForward = async () => {
    if (step === 1) {
      // Proceed from step 1 to step 2
      setStep(2);
    } else if (step === 2) {
      // Check if shipping info is valid
      if (shippingInfoValid) {
        // If shipping has been modified or newly created, save it
        if (shippingFormTouched) {
          try {
            setShippingError(null);
            setUpdatingShipping(true);
            
            // Get country code (either from selected dropdown or custom input)
            let countryCode;
            if (selectedCountry === 'custom') {
              countryCode = customCountry.substring(0, 2).toUpperCase();
            } else {
              countryCode = selectedCountry;
            }
            
            const apiPayload = {
              name: `${shippingInfo.first_name} ${shippingInfo.last_name}`,
              email: shippingInfo.email,
              country_code: countryCode,
              national_number: shippingInfo.phone_number,
              phone_number: shippingInfo.phone_number,
              shipping_address_line1: shippingInfo.address_line_1,
              shipping_address_line2: shippingInfo.address_line_2 || '',
              shipping_city: shippingInfo.city,
              shipping_state: shippingInfo.state,
              shipping_zip: shippingInfo.postal_code
            };
            
            const response = await apiClient.post(ENDPOINTS.USER_SHIPPING_INFO, apiPayload);
            
            if (response.data) {
              // Successfully saved shipping info
              setStep(3);
            }
          } catch (error) {
            console.error('Error saving shipping info:', error);
            setShippingError('Failed to save shipping information. Please try again.');
          } finally {
            setUpdatingShipping(false);
          }
        } else {
          // Shipping info is valid but wasn't modified, just proceed
          setStep(3);
        }
      } else {
        setShippingError('Please fill in all required fields.');
      }
    } else if (step === 3) {
      // Handle step 3 submission
      await handleSubmit();
    }
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setReplyError(null);
      await onSubmit(replyText, replyMedia, replyGif);

      // Track TikTok events after successful submission
      if (currentUser) {
        try {
          // Identify user with hashed PII data
          await identifyTikTokUser({
            email: currentUser.email,
            phone_number: shippingInfo.phone_number,
            external_id: currentUser.id
          });

          // Track completion of registration
          trackCompleteRegistration({
            contents: [{
              content_id: String(originalPost.id),
              content_type: 'product',
              content_name: originalPost.content || 'Collaboration Application'
            }],
            value: 0, // Since this is an application, value is 0
            currency: 'USD'
          });
        } catch (error) {
          console.error('Error tracking TikTok events:', error);
          // Don't block the submission if tracking fails
        }
      }

      onClose();
    } catch (error) {
      console.error('Error submitting application:', error);
      setReplyError('Unable to submit your application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add this helper function for country code conversion
  const getCountryCode = (countryName: string): string => {
    const countryMap: {[key: string]: string} = {
      'United States': 'US',
      'Canada': 'CA',
      'United Kingdom': 'GB',
      'Australia': 'AU',
      'Germany': 'DE',
      'France': 'FR',
      'Italy': 'IT',
      'Spain': 'ES',
      'Japan': 'JP',
      'China': 'CN',
      'India': 'IN',
      'Brazil': 'BR',
      'Mexico': 'MX',
      'South Korea': 'KR',
      // Add more country mappings as needed
    };
    
    // If country is in the map, return its code
    if (countryMap[countryName]) {
      return countryMap[countryName];
    }
    
    // Otherwise return first two letters uppercase (simple fallback)
    return countryName.substring(0, 2).toUpperCase();
  };

  // Add these functions
  const handleTextChange = (newText: string) => {
    setReplyContent(newText);
    setReplyText(newText); // Also update the original replyText state
  };

  const handleFileSelection = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const target = e.target;
    if (!target.files || target.files.length === 0) return;
    
    try {
      const { success, errors } = await processMediaFiles(target.files);
      
      if (errors.length > 0) {
        errors.forEach(error => setToastMessage(error));
      }
      
      if (success.length > 0) {
        setMediaList(prev => [...prev, ...success]);
        setReplyMedia(prev => ([...success.map(s => s.file).filter(Boolean) as File[]])); // Update original state
      }
    } catch (error) {
      console.error("Error processing files:", error);
      setToastMessage("Error uploading media. Please try again.");
    }
    
    // Reset the file input value
    if (target) {
      target.value = '';
    }
  };

  const handleReplySubmit = async () => {
    if (!isSubmitEnabled || !currentUser) {
      setReplyError('Please add content to your application.');
      return;
    }

    setIsPosting(true);
    setReplyError(null);

    try {
      // Convert mediaList to File array for the onSubmit function
      const mediaFiles = mediaList.map(media => media.file).filter(Boolean) as File[];
      
      await onSubmit(replyContent, mediaFiles, selectedGif);
      
      // Clear form and close modal on success
      setReplyContent('');
      setMediaList([]);
      setSelectedGif(null);
      onClose();
    } catch (error) {
      console.error('Error submitting application:', error);
      setReplyError('Failed to submit application. Please try again.');
    } finally {
      setIsPosting(false);
    }
  };

  return createPortal(
    <div className="apply-collabs-modal-overlay">
      <div className="apply-collabs-modal-container">
        <VIModal isOpen={isOpen} onClose={onClose} title="Apply to Collab">
          <div className="apply-collabs-steps">
            <div className="apply-collabs-header-actions">
              <button 
                className="apply-collabs-back-button" 
                onClick={handleStepBack}
              >
                {step === 1 ? 'Cancel' : 'Back'}
              </button>
              
              <button 
                className="apply-collabs-next-button" 
                onClick={handleStepForward}
                disabled={
                  (step === 1 && (!tiktokVerified || confirmingIdentity)) ||
                  (step === 2 && (!shippingInfoValid || updatingShipping)) ||
                  (step === 3 && (isSubmitting || (!replyContent.trim() && mediaList.length === 0 && !selectedGif)))
                }
              >
                {step === 3 
                  ? (isSubmitting ? 'Submitting...' : 'Submit Application') 
                  : (step === 2 && updatingShipping ? 'Updating...' : 'Next')}
              </button>
            </div>
            
            <h2>Apply to Collab</h2>
            
            <div className="steps-indicator">
              <div className={`step ${step >= 1 ? 'active' : ''}`}>1</div>
              <div className="step-line"></div>
              <div className={`step ${step >= 2 ? 'active' : ''}`}>2</div>
              <div className="step-line"></div>
              <div className={`step ${step >= 3 ? 'active' : ''}`}>3</div>
            </div>

            <div className="apply-collabs-content-scrollable">
              {/* Step 1: TikTok Profile Verification */}
              {step === 1 && (
                <div className="step-content">
                  <h3>Verify Your TikTok Profile</h3>
                  
                  {checkingPreVerification ? (
                    <div className="checking-verification">
                      <p>Checking your TikTok verification status...</p>
                    </div>
                  ) : confirmingIdentity ? (
                    <div className="confirm-identity">
                      <p>We found you already have a verified TikTok profile:</p>
                      <div className="tiktok-identity">
                        <div className="tiktok-verified-info">
                          <p><strong>Username:</strong> @{tiktokUsername}</p>
                          <p><strong>Followers:</strong> {tiktokFollowers}</p>
                        </div>
                        {tiktokVerificationImage && (
                          <div className="tiktok-verification-image">
                            <img src={tiktokVerificationImage} alt="TikTok Profile Verification" />
                          </div>
                        )}
                      </div>
                      <p>Is this your TikTok profile?</p>
                      <div className="identity-confirmation-buttons">
                        <button 
                          className={`apply-collabs-confirm-button ${confirmationSelection === 'yes' ? 'active' : ''}`} 
                          onClick={() => confirmTikTokIdentity(true)}
                        >
                          Yes, this is me
                        </button>
                        <button 
                          className={`apply-collabs-reject-button ${confirmationSelection === 'no' ? 'active' : ''}`} 
                          onClick={() => confirmTikTokIdentity(false)}
                        >
                          No, that's not me
                        </button>
                      </div>
                    </div>
                  ) : tiktokVerified ? (
                    <div className="verification-success">
                      <p>✓ Verified TikTok profile: @{tiktokUsername}</p>
                      <p>Followers: {tiktokFollowers}</p>
                      {tiktokVerificationImage && (
                        <div className="tiktok-verification-image">
                          <img src={tiktokVerificationImage} alt="TikTok Profile Verification" className="verification-thumbnail" />
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <p>Please upload a screenshot of your TikTok profile showing your username and follower count.</p>
                      
                      <div className="tiktok-upload-section">
                        {tiktokScreenshotUrl ? (
                          <div className="tiktok-screenshot-preview">
                            <img src={tiktokScreenshotUrl} alt="TikTok Profile" />
                            <button className="apply-collabs-change-button" onClick={() => setTiktokScreenshotUrl(null)}>Change Image</button>
                          </div>
                        ) : (
                          <div className="upload-tiktok-box">
                            <input
                              type="file"
                              accept="image/*"
                              id="tiktok-screenshot"
                              onChange={handleTiktokScreenshotSelect}
                              style={{ display: 'none' }}
                            />
                            <label htmlFor="tiktok-screenshot" className="upload-button">
                              Select Screenshot
                            </label>
                          </div>
                        )}
                      </div>

                      {tiktokError && <p className="error-message">{tiktokError}</p>}
                      
                      {tiktokScreenshotUrl && !tiktokVerified && (
                        <button 
                          className="apply-collabs-verify-button" 
                          onClick={verifyTiktokScreenshot} 
                          disabled={tiktokVerifying}
                        >
                          {tiktokVerifying ? 'Verifying...' : 'Verify TikTok Profile'}
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Step 2: Shipping Information */}
              {step === 2 && (
                <div className="step-content">
                  <h3>Shipping Information</h3>
                  <p>We need your shipping details to send you products for this collab.</p>
                  
                  {loadingShipping ? (
                    <div className="loading-shipping">Loading your information...</div>
                  ) : (
                    <form className="shipping-form" onSubmit={(e) => e.preventDefault()}>
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="first_name">First Name *</label>
                          <input
                            type="text"
                            id="first_name"
                            name="first_name"
                            value={shippingInfo.first_name}
                            onChange={handleShippingInfoChange}
                            required
                          />
                        </div>
                        
                        <div className="form-group">
                          <label htmlFor="last_name">Last Name *</label>
                          <input
                            type="text"
                            id="last_name"
                            name="last_name"
                            value={shippingInfo.last_name}
                            onChange={handleShippingInfoChange}
                            required
                          />
                        </div>
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="email">Email *</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={shippingInfo.email}
                          onChange={handleShippingInfoChange}
                          required
                          disabled={updatingShipping}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="phone_number">Phone Number *</label>
                        <input
                          type="tel"
                          id="phone_number"
                          name="phone_number"
                          value={shippingInfo.phone_number}
                          onChange={handleShippingInfoChange}
                          required
                        />
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="address_line_1">Address Line 1 *</label>
                        <input
                          type="text"
                          id="address_line_1"
                          name="address_line_1"
                          value={shippingInfo.address_line_1}
                          onChange={handleShippingInfoChange}
                          required
                        />
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="address_line_2">Address Line 2</label>
                        <input
                          type="text"
                          id="address_line_2"
                          name="address_line_2"
                          value={shippingInfo.address_line_2}
                          onChange={handleShippingInfoChange}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="country">Country *</label>
                        <div className="select-wrapper">
                          <select 
                            id="country"
                            value={selectedCountry}
                            onChange={handleCountryChange}
                            className={selectedCountry ? 'input-filled' : ''}
                            required
                          >
                            <option value="">Select country</option>
                            {countries.map((country) => (
                              <option key={country.isoCode} value={country.isoCode}>
                                {country.name}
                              </option>
                            ))}
                            <option value="custom">Other (Specify below)</option>
                          </select>
                          <img src={DropdownIcon} alt="Dropdown Icon" className="dropdown-icon" />
                        </div>
                        {selectedCountry === 'custom' && (
                          <input
                            type="text"
                            value={customCountry}
                            onChange={handleCustomCountryChange}
                            placeholder="Enter custom country"
                            className="custom-input"
                            required
                          />
                        )}
                      </div>
                      
                      <div className="form-row">
                        <div className="form-group">
                          <label htmlFor="state">State/Province *</label>
                          <div className="select-wrapper">
                            <select 
                              id="state"
                              value={selectedState}
                              onChange={handleStateChange}
                              className={selectedState ? 'input-filled' : ''}
                              required
                            >
                              <option value="">Select state</option>
                              {selectedCountry && selectedCountry !== 'custom' ? (
                                states.map((state) => (
                                  <option key={state.isoCode} value={state.isoCode}>
                                    {state.name}
                                  </option>
                                ))
                              ) : (
                                <option disabled>Please select country first</option>
                              )}
                              <option value="custom">Other (Specify below)</option>
                            </select>
                            <img src={DropdownIcon} alt="Dropdown Icon" className="dropdown-icon" />
                          </div>
                          {selectedState === 'custom' && (
                            <input
                              type="text"
                              value={customState}
                              onChange={handleCustomStateChange}
                              placeholder="Enter custom state"
                              className="custom-input"
                              required
                            />
                          )}
                        </div>
                        
                        <div className="form-group">
                          <label htmlFor="city">City *</label>
                          <div className="select-wrapper">
                            <select 
                              id="city"
                              value={selectedCity}
                              onChange={handleCityChange}
                              className={selectedCity ? 'input-filled' : ''}
                              required
                            >
                              <option value="">Select city</option>
                              {selectedState && selectedState !== 'custom' ? (
                                cities.map((city) => (
                                  <option key={city.name} value={city.name}>
                                    {city.name}
                                  </option>
                                ))
                              ) : (
                                <option disabled>Please select state first</option>
                              )}
                              <option value="custom">Other (Specify below)</option>
                            </select>
                            <img src={DropdownIcon} alt="Dropdown Icon" className="dropdown-icon" />
                          </div>
                          {selectedCity === 'custom' && (
                            <input
                              type="text"
                              value={customCity}
                              onChange={handleCustomCityChange}
                              placeholder="Enter custom city"
                              className="custom-input"
                              required
                            />
                          )}
                        </div>
                      </div>
                      
                      <div className="form-group">
                        <label htmlFor="postal_code">Zip/Postal Code *</label>
                        <input
                          type="text"
                          id="postal_code"
                          name="postal_code"
                          value={shippingInfo.postal_code}
                          onChange={handleShippingInfoChange}
                          required
                        />
                      </div>
                    </form>
                  )}
                  
                  {shippingError && <p className="error-message">{shippingError}</p>}
                </div>
              )}

              {/* Step 3: Reply Message */}
              {step === 3 && (
                <div className="step-content">
                  <h3>Why do you want to be part of this collab?</h3>
                  <p>Tell the brand why you're a great fit for this collaboration.</p>
                  
                  <div className="reply-input-container">
                    <PostReplyInput
                      content={replyContent}
                      onContentChange={handleTextChange}
                      currentUser={currentUser}
                      originalPostUsername={originalPost.user.username}
                      ref={textAreaRef}
                    />
                    <MediaManager
                      mediaList={mediaList}
                      selectedGif={selectedGif}
                      onMediaListChange={setMediaList}
                      onGifChange={setSelectedGif}
                      onError={setToastMessage}
                      maxItems={10}
                    />

                    <div className="post-reply-footer">
                      <div className="post-reply-actions">
                        <input
                          type="file"
                          accept="image/*, video/mp4, video/quicktime"
                          multiple
                          onChange={handleFileSelection}
                          style={{ display: 'none' }}
                          id="reply-media-upload"
                        />
                        <label htmlFor="reply-media-upload" className="post-action-button">
                          <img src={icon_newpost_images} alt="Upload Media" className="post-action-icon" />
                        </label>
                        <button className="post-action-button" onClick={() => setShowCamera(true)}>
                          <img src={icon_newpost_camera} alt="Camera" className="post-action-icon" />
                        </button>
                        <button className="post-action-button" onClick={() => setShowGiphyModal(true)}>
                          <img src={icon_newpost_gif} alt="GIF" className="post-action-icon" />
                        </button>
                      </div>

                    </div>
                  </div>
                  
                  {replyError && <p className="error-message">{replyError}</p>}
                </div>
              )}
            </div>
          </div>
        </VIModal>
        {showCamera && (
          <Camera
            onCapture={(imageSrc: string) => {
              const newMedia = processCameraCapture(imageSrc);
              setMediaList(prev => [...prev, newMedia]);
              setShowCamera(false);
            }}
            onClose={() => setShowCamera(false)}
          />
        )}
        <GiphyModal
          isOpen={showGiphyModal}
          onSelect={(gifUrl) => {
            setSelectedGif(gifUrl);
            setShowGiphyModal(false);
          }}
          onClose={() => setShowGiphyModal(false)}
        />
        {toastMessage && (
          <VIToast
            message={toastMessage}
            onClose={() => setToastMessage(null)}
          />
        )}
      </div>
    </div>,
    document.getElementById('modal-root') || document.body
  );
};

export default ApplyCollabsStepsModal; 